<template>
  <SubscribeAccordionItem
    class="subscribe__step two"
    :isOpen="isOpen"
    :disabled="disabled"
    disabled-tooltip-id="subscribe_step_2_disabled_tooltip"
    @toggle="$emit('toggle')"
  >
    <template #head>
      <div>
        <div class="subscribe__step-label" v-if="!isDone">{{t('label')}}</div>
        <div class="subscribe__step-done" v-else>
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none" role="img" :aria-label="t('stepDoneAlt')">
            <rect width="34" height="34" rx="17" fill="#D7F3D2"/>
            <path d="M9 16.4L14.6 23L25 11" stroke="#04695D" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
          </svg>
        </div>

        <h3 class="subscribe__step-header h6">
          <LegalText :text="t('header')" />
        </h3>
        <div class="subscribe__step-subheader">{{ t('subheader') }}</div>
      </div>
    </template>

    <template #toggler-content>
      <div
        v-if="disabled"
        id="subscribe_step_2_disabled_tooltip"
        class="subscribe__step-toggler--tooltip"
        role="tooltip"
        v-html="t('tooltip')"
      ></div>

      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="10" viewBox="0 0 20 10" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd"
          d="M19.6711 9.66434C20.1227 9.22954 20.1073 8.53882 19.6367 8.12159L10.8178 0.303836C10.3608 -0.101278 9.63923 -0.101278 9.18223 0.303836L0.363338 8.12159C-0.107325 8.53882 -0.122746 9.22954 0.328894 9.66434C0.780533 10.0991 1.52821 10.1134 1.99887 9.69616L10 2.60334L18.0011 9.69616C18.4718 10.1134 19.2195 10.0991 19.6711 9.66434Z"
          fill="#2A2C2C"
        />
      </svg>
    </template>

    <div class="subscribe__step-period">
      <template v-if="hasSubscription">
        <div class="input-group">
          <label for="subscribe_period">{{ t('subscriptionTerm') }}</label>
          <input 
            id="subscribe_period" 
            type="text" 
            :value="periodLabel" 
            readonly 
            aria-describedby="subscribe_period_tooltip"
          />
          <span id="subscribe_period_tooltip" class="tooltip">
            Період активної підписки змінити неможливо
          </span>
        </div>

        <div class="input-group">
          <label for="subscribe_date_end">{{ t('endSubscribeLabel') }}</label>
          <input
            id="subscribe_date_end"
            type="text"
            :value="displayEnd"
            readonly
            aria-describedby="subscribe_date_end_tooltip"
          />
          <span id="subscribe_date_end_tooltip" class="tooltip">
            Період активної підписки змінити неможливо
          </span>
        </div>
      </template>

      <template v-else>
        <div class="input-group">
          <label for="subscribe_date_start">{{ t('startSubscribeLabel') }}</label>
          <input
            id="subscribe_date_start"
            type="text"
            :value="displayStart"
            readonly
          />
        </div>

        <div class="input-group">
          <label for="subscription-period" id="subscription-period-ts-label">{{t('subscribePeriodLabel')}}</label>

          <div class="select">
            <select
              id="subscription-period"
              ref="selectEl"
              name="period"
              :value="displayPeriodMonths"
              :aria-label="t('subscribePeriodLabel')"
              @change="onNativeChange"
            >
              <option value="3">{{t('threeMonths')}}</option>
              <option value="4">{{ t('fourMonths') }}</option>
            </select>

            <div class="select__icon select__icon--arrow" role="presentation">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M26.3974 10.9363C26.7565 11.2954 26.7565 11.8776 26.3974 12.2367L17.5698 21.0643C17.3974 21.2368 17.1635 21.3337 16.9196 21.3337C16.6757 21.3337 16.4419 21.2368 16.2694 21.0643L7.44182 12.2367C7.08272 11.8776 7.08272 11.2954 7.44182 10.9363C7.80093 10.5772 8.38315 10.5772 8.74225 10.9363L16.9196 19.1137L25.097 10.9363C25.4561 10.5772 26.0383 10.5772 26.3974 10.9363Z"
                  fill="#2A2C2C"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="input-group">
          <label for="subscribe_date_end">{{t('endSubscribeLabel')}}</label>
          <input
            id="subscribe_date_end"
            type="text"
            :value="displayEnd"
            readonly
          />
        </div>
      </template>
    </div>
  </SubscribeAccordionItem>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import TomSelect from 'tom-select'
import SubscribeAccordionItem from './SubscribeAccordionItem.vue'
import LegalText from '../../js/components/LegalText.vue'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  isDone: { type: Boolean, default: false },

  startDate: { type: String, default: '' },
  periodMonths: { type: String, default: '' },

  hasSubscription: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },

  subscription: { type: Object, default: null },
})
const emit = defineEmits(['toggle', 'update:startDate', 'update:periodMonths'])

const i18n = {
  uk: {
    label: '2 крок',
    header: 'Вибери дату початку@legal(period) та період дії послуги',
    subheader: 'У кожного учасника може бути лише одна діюча підписка',
    tooltip: ' Буде доступний після <span class="block">заповнення <span class="orange">1 кроку</span></span>',
    stepDoneAlt: 'Крок пройдено',
    startSubscribeLabel: 'Дата початку',
    endSubscribeLabel: 'Дата закінчення',
    subscribePeriodLabel: 'Період підписки',
    subscriptionTerm: 'Термін дії підписки',
    threeMonths: '3 місяці',
    fourMonths: '4 місяці'
  },
  ru: {
    label: '2 шаг',
    header: 'Выбери дату начала@legal(period) и период действия услуги',
    subheader: 'У каждого участника может быть только одна действующая подписка',
    tooltip: ' Будет доступен после <span class="block">заполнения <span class="orange">1 шага</span></span>',
    stepDoneAlt: 'Шаг пройден',
    startSubscribeLabel: 'Дата начала',
    endSubscribeLabel: 'Дата окончания',
    subscribePeriodLabel: 'Период подписки',
    subscriptionTerm: 'Срок действия подписки',
    threeMonths: '3 месяца',
    fourMonths: '4 месяца'
  }
}
const locale = computed(() => window.Shopify?.locale || 'uk')
const t = (key) => i18n[locale.value]?.[key] || key

function todayISO() {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 10)
}

function normalizeIsoDate(any) {
  const s = String(any || '').trim()
  if (!s) return ''
  return s.slice(0, 10)
}

function parseISOToLocalDate(iso) {
  if (!iso) return null
  return new Date(`${iso}T00:00:00`)
}

function toISODateLocal(d) {
  const local = new Date(d)
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset())
  return local.toISOString().slice(0, 10)
}

function addMonths(date, months) {
  const d = new Date(date)
  const day = d.getDate()
  d.setMonth(d.getMonth() + months)
  if (d.getDate() !== day) d.setDate(0)
  return d
}

function formatUaDate(iso) {
  if (!iso || typeof iso !== 'string') return ''
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return iso
  return `${d}.${m}.${y}`
}

const sub = computed(() => props.subscription || null)

const subMonths = computed(() => {
  const raw =
    props.subscription?.months ??
    props.subscription?.period_months ??
    props.subscription?.periodMonths

  return raw == null ? '' : String(raw)
})

const subEndISO = computed(() =>
  normalizeIsoDate(sub.value?.end_date || sub.value?.date_end || sub.value?.endDate)
)

const subStartISO = computed(() =>
  normalizeIsoDate(sub.value?.start_date || sub.value?.date_start || sub.value?.startDate)
)

const displayPeriodMonths = computed(() =>
  String((props.hasSubscription ? subMonths.value : props.periodMonths) || '3')
)

const periodLabel = computed(() => {
  const m = Number(displayPeriodMonths.value || 0)
  if (!m) return ''
  return `${m} місяці`
})

const endDateISO = computed(() => {
  if (props.hasSubscription) {
    if (subEndISO.value) return subEndISO.value

    const start = parseISOToLocalDate(subStartISO.value)
    const m = Number(subMonths.value) || 0
    if (!start || !m) return ''
    return toISODateLocal(addMonths(start, m))
  }

  const start = parseISOToLocalDate(props.startDate)
  const m = Number(props.periodMonths) || 0
  if (!start || !m) return ''
  return toISODateLocal(addMonths(start, m))
})

const displayStart = computed(() => formatUaDate(props.startDate))
const displayEnd = computed(() => formatUaDate(endDateISO.value))

async function validateStep() {
  if (props.isLoading) return false
  if (props.hasSubscription) return true

  let valid = true

  if (!props.startDate) {
    emit('update:startDate', todayISO())
  }

  if (!props.periodMonths) {
    emit('update:periodMonths', '3')
    valid = false
  }

  await nextTick()
  return valid
}

defineExpose({
  validateStep,
})

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    if (props.isLoading) return
    if (props.hasSubscription) return

    if (!props.startDate) emit('update:startDate', todayISO())
    if (!props.periodMonths) emit('update:periodMonths', '3')
  },
  { immediate: true }
)

const selectEl = ref(null)
let periodSelect = null

function destroyPeriodSelect() {
  if (!periodSelect) return
  periodSelect.destroy()
  periodSelect = null
}

async function initPeriodSelect() {
  if (periodSelect || !selectEl.value) return
  if (props.hasSubscription) return
  if (props.isLoading) return

  const nativeSelect = selectEl.value
  const labelId = 'subscription-period-ts-label'
  const labelText = t('subscribePeriodLabel')

  periodSelect = new TomSelect(nativeSelect, {
    controlInput: null,
    searchField: [],
    create: false,
    onChange: (val) => emit('update:periodMonths', String(val || '')),
    onInitialize() {
      const control = this.control
      const dropdown = this.dropdown_content || this.dropdown
      const labelEl = document.getElementById(labelId)

      nativeSelect.setAttribute('aria-label', labelText)

      if (labelEl) {
        labelEl.setAttribute('for', 'subscription-period')
      }

      if (control) {
        control.setAttribute('aria-labelledby', labelId)
      }

      if (dropdown) {
        dropdown.setAttribute('aria-labelledby', labelId)
      }
    },
  })

  periodSelect.setValue(String(props.periodMonths || '3'), true)
}

function onNativeChange(e) {
  emit('update:periodMonths', String(e.target?.value || ''))
}

watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      await nextTick()
      await initPeriodSelect()
    } else {
      destroyPeriodSelect()
    }
  },
  { immediate: true }
)

watch(
  () => props.periodMonths,
  (val) => {
    if (!periodSelect) return
    if (periodSelect.getValue() !== String(val || '3')) {
      periodSelect.setValue(String(val || '3'), true)
    }
  }
)

watch(
  () => props.hasSubscription,
  (has) => {
    if (has) destroyPeriodSelect()
    else if (props.isOpen && !props.isLoading) nextTick().then(initPeriodSelect)
  }
)

watch(
  () => props.isLoading,
  (loading) => {
    if (!loading && props.isOpen && !props.hasSubscription) {
      nextTick().then(initPeriodSelect)
    }
  }
)

onBeforeUnmount(() => destroyPeriodSelect())
</script>
